import React from 'react';
import PropTypes from 'prop-types';

import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useStore } from 'effector-react';

import { Button } from '@oca/ui';
import { TextFieldWrapper } from '@lib/final-form-oca-ui';
import { Trans } from '@lingui/macro';
import { $employeeList } from '@features/common';
import { Box, Flex } from 'rebass';
import styled from 'styled-components';

import { FieldsetContact } from '../molecules';
import { createContactModel } from '../model/static';

const normalize = v => (v === null || v === undefined ? '' : String(v).trim());

const required = message => value => {
  return normalize(value) ? undefined : message;
};

const composeValidators = (...validators) => (value, allValues, meta) =>
  validators.reduce(
    (error, validator) => error || validator(value, allValues, meta),
    undefined,
  );

const uniqueBy = (list, getter, message, initialValuesGetter) => (
  value,
  allValues,
  meta,
) => {
  const val = normalize(value);
  if (!val) return undefined;

  // Use meta.initial first (best for edit pages)
  const initialFieldVal = normalize(meta && meta.initial);

  // fallback to initialValues.* if you want
  const initialFromProps = initialValuesGetter
    ? normalize(initialValuesGetter())
    : '';

  // unchanged => skip uniqueness check
  if (
    (initialFieldVal && initialFieldVal === val) ||
    (initialFromProps && initialFromProps === val)
  ) {
    return undefined;
  }

  const arr = Array.isArray(list) ? list : [];
  const dup = arr.some(item => normalize(getter(item)) === val);

  return dup ? message : undefined;
};

export function FormContacts({ initialValues }) {
  const employeeList = useStore($employeeList);

  return (
    <div>
      {/* Contact & Address Information (3 inputs per row) */}
      <SectionContainer>
        <SectionHeader>
          <SectionTitleText>
            <Trans>Contact &amp; Address Details</Trans>
          </SectionTitleText>
        </SectionHeader>
        <FormGrid>
          <FormCol>
            <Field
              type="tel"
              name="mobilePhone"
              placeholder="+965 1234 1234"
              component={TextFieldWrapper}
              label={<Trans>Mobile phone</Trans>}
              helperText={<Trans>Primary personal number</Trans>}
              spaceAfter
              validate={composeValidators(
                required('This field is required'),
                uniqueBy(
                  employeeList,
                  emp => emp.mobilePhone,
                  'This mobile phone already exists',
                  () => initialValues && initialValues.mobilePhone,
                ),
              )}
            />
          </FormCol>
          <FormCol>
            <Field
              type="tel"
              name="workPhone"
              component={TextFieldWrapper}
              label={<Trans>Work phone</Trans>}
              placeholder="+965 1234 1234"
              helperText=" "
              spaceAfter
            />
          </FormCol>
          <FormCol>
            <Field
              type="tel"
              name="homePhone"
              component={TextFieldWrapper}
              label={<Trans>Home phone</Trans>}
              placeholder="+965 1234 1234"
              helperText=" "
              spaceAfter
            />
          </FormCol>
        </FormGrid>
        <FormGrid>
          <FormCol>
            <Field
              type="email"
              name="email"
              component={TextFieldWrapper}
              label={<Trans>Email address</Trans>}
              helperText=" "
              spaceAfter
              validate={composeValidators(
                required('This field is required'),
                uniqueBy(
                  employeeList,
                  emp => emp.email,
                  'This email already exists',
                  () => initialValues && initialValues.email,
                ),
              )}
            />
          </FormCol>
          <FormCol>
            <Field
              name="street"
              label={<Trans>Street</Trans>}
              component={TextFieldWrapper}
              helperText=" "
              required
              spaceAfter
            />
          </FormCol>
          <FormCol>
            <Field
              name="city"
              label={<Trans>City</Trans>}
              component={TextFieldWrapper}
              helperText=" "
              spaceAfter
              required
            />
          </FormCol>
        </FormGrid>
        <FormGrid>
          <FormCol>
            <Field
              name="country"
              label={<Trans>Country</Trans>}
              component={TextFieldWrapper}
              helperText=" "
              spaceAfter
              required
            />
          </FormCol>
        </FormGrid>
      </SectionContainer>

      {/* Emergency Contacts */}
      <FieldArray
        name="contacts"
        render={({ fields }) => (
          <SectionContainer>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              mb={2}
              flexWrap="wrap"
              style={{ gap: 8 }}
            >
              <Box>
                <SectionTitleText>
                  <Trans>Emergency Contacts</Trans>
                </SectionTitleText>
              </Box>
              <Button
                type="button"
                variant="secondary"
                onClick={() => fields.push(createContactModel())}
              >
                + <Trans>Add Emergency Contact</Trans>
              </Button>
            </Flex>
            {fields.value && fields.value.length < 1 && (
              <EmptyNotice>
                <Trans>There are no emergency contacts yet.</Trans>
              </EmptyNotice>
            )}
            <EmergencyGrid>
              {fields.map((fieldName, index) => (
                <ContactCard key={fieldName}>
                  <ContactCardHeader>
                    <ContactNumberBadge>
                      <Trans>Contact</Trans> #{index + 1}
                    </ContactNumberBadge>
                    {index > 0 && (
                      <RemoveButton
                        type="button"
                        onClick={() => fields.remove(index)}
                      >
                        <Trans>Remove</Trans>
                      </RemoveButton>
                    )}
                  </ContactCardHeader>
                  <FieldsetContact name={fieldName} />
                </ContactCard>
              ))}
            </EmergencyGrid>
          </SectionContainer>
        )}
      />
    </div>
  );
}

const SectionContainer = styled.div`
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 12px;
`;

const SectionTitleText = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 2px 0;
  letter-spacing: -0.01em;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormCol = styled.div`
  width: 100%;
  min-width: 0;
`;

const EmergencyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyNotice = styled.div`
  padding: 16px;
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  color: #64748b;
  font-size: 13px;
  margin-bottom: 12px;
`;

const ContactCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 12px;
`;

const ContactCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
`;

const ContactNumberBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;

  &:hover {
    background: #fee2e2;
  }
`;

FormContacts.propTypes = {
  initialValues: PropTypes.shape({
    email: PropTypes.string,
    mobilePhone: PropTypes.string,
  }).isRequired,
};
